import {
  type CalendarDate,
  type ZonedDateTime,
  getLocalTimeZone,
  now,
  today,
} from "@internationalized/date";
import cn from "classnames";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import type { InputHTMLAttributes } from "react";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateRangePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
  RangeCalendar,
  TimeField,
} from "react-aria-components";
import type {
  DatePickerProps,
  DateRangePickerProps,
  DateValue,
} from "react-aria-components";
import CurrencyInput from "react-currency-input-field";
import {
  IconCalendar,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
} from "./icons";
import { tokens } from "./tokens";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const classes = cn(
    "appearance-none outline-none",
    "leading-none",
    "p-3",
    "hover:border-gray-500",
    "focus:ring-gray-500 focus:ring-2 focus:ring-offset-2 focus:border-gray-500",
    "border border-gray-300 placeholder-gray-400 rounded-md shadow-sm",
    "disabled:cursor-not-allowed disabled:opacity-50",
    props.className,
  );
  return (
    <input {...props} aria-label={props.name} ref={ref} className={classes} />
  );
});

interface InputCurrencyProps
  extends Omit<
    InputProps,
    | "type"
    | "inputMode"
    | "value"
    | "defaultValue"
    | "step"
    | "onChange"
    | "onFocus"
    | "onBlur"
  > {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

// Without this the package formats with the browser's locale.
const USD_LOCALE = { locale: "en-US" };

const parseAmount = (text: string) => {
  const amount = Number.parseFloat(text);
  return Number.isNaN(amount) ? undefined : amount;
};

/**
 * Edits a USD amount, masked as `$1,234.5` while typing and padded to
 * `$1,234.50` once it loses focus. An empty field is `undefined`.
 */
export function InputCurrency({
  value,
  onChange,
  ...props
}: InputCurrencyProps) {
  const [isFocused, setIsFocused] = useState(false);
  // The package only keeps a trailing point like "25." if it's handed the text.
  const [text, setText] = useState("");

  const display = () => {
    if (value === undefined) return "";
    if (!isFocused) return value.toFixed(2);
    if (parseAmount(text) === value) return text;
    // Round to cents so the text always fits the field's two decimals.
    return `${Number(value.toFixed(2))}`;
  };

  return (
    <CurrencyInput
      {...props}
      customInput={Input}
      autoComplete="off"
      intlConfig={USD_LOCALE}
      prefix="$"
      decimalsLimit={2}
      decimalScale={isFocused ? undefined : 2}
      allowNegativeValue={false}
      disableAbbreviations
      formatValueOnBlur={false}
      value={display()}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onValueChange={(next) => {
        const nextText = next === undefined ? "" : next;
        setText(nextText);
        onChange(parseAmount(nextText));
      }}
    />
  );
}

interface InputSearchProps extends React.HTMLProps<HTMLDivElement> {
  search: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export const InputSearch = ({
  search,
  onChange,
  placeholder = "Search...",
  className = "w-[300px] h-[36px]",
  autoFocus = false,
  ...props
}: InputSearchProps) => {
  return (
    <div {...props} className={`flex relative ${className}`}>
      <IconSearch
        className="absolute top-[10px] left-[10px]"
        color="#595E63"
        variant="sm"
      />
      <Input
        placeholder={placeholder}
        type="text"
        value={search}
        onChange={onChange}
        autoFocus={autoFocus}
        className="pl-9 w-full"
      />
    </div>
  );
};

export const TextArea = ({
  className,
  autoResize = false,
  ...props
}: {
  autoResize?: boolean;
} & React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !autoResize) return;

    const resize = () => {
      // Reset the height to auto and then set it to the scroll height + 20px
      // (our base-text line height).
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + 20}px`;
    };

    // Initial resize to fit the content.
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(el);

    return () => ro.disconnect();
  }, [autoResize, props.value]);

  return (
    <textarea
      className={cn(
        tokens.type.textarea,
        className,
        autoResize && "resize-none overflow-hidden",
      )}
      {...props}
      ref={ref}
    />
  );
};

export const CheckBox = ({
  className,
  label,
  labelClassName,
  variant = "default",
  variantSize = "base",
  ...props
}: InputProps & {
  label: React.ReactNode;
  labelClassName?: string;
  variant?: "default" | "simple";
  variantSize?: "base" | "sm";
}) => {
  if (variant === "simple") {
    const { checked, ...rest } = props as any;
    return (
      <label className="flex items-center w-fit">
        <input
          type="checkbox"
          className={cn("sr-only", variantSize === "sm" && "px-1 py-1")}
          checked={checked}
          {...rest}
        />
        <span
          className={cn(
            "w-5 h-5 flex items-center justify-center rounded text-black",
            className,
          )}
        >
          {checked ? <IconCheck variant="sm" color="#171717" /> : null}
        </span>
        <span className={cn("ml-1", labelClassName)}>{label}</span>
      </label>
    );
  }
  return (
    <label className="flex w-fit">
      <Input
        type="checkbox"
        className={cn(
          "rounded-lg",
          variantSize === "base" && "h-6",
          variantSize === "sm" && "h-4 px-2 py-2 my-0.5",
          className,
        )}
        {...props}
      />
      <span className={cn("ml-1", labelClassName)}>{label}</span>
    </label>
  );
};

interface InputDateProps<T extends DateValue> extends DatePickerProps<T> {}

const baseDatePickerClasses = [
  "max-w-fit",
  "data-[disabled]:disabled:opacity-50",
  "data-[disabled]:opacity-50",
  "data-[disabled]:cursor-not-allowed",
];

// The date input field
const dateInputClasses = cn(
  "appearance-none outline-none",
  "p-3",
  "pr-10",
  "w-full",
  "leading-none",
  "border border-gray-300 placeholder-gray-400 rounded-md shadow-sm",
  "data-[hovered]:border-gray-500",
  "data-[focus-within]:border-gray-500",
  "data-[focus-within]:ring-1",
  "data-[focus-within]:ring-gray-500",
  "data-[disabled]:opacity-50",
);

// Container style for range picker so the two inputs look like one field
const rangeGroupClasses = cn(
  "appearance-none outline-none",
  "px-3 py-2",
  "pr-10",
  "w-full",
  "leading-none",
  "border border-gray-300 rounded-md shadow-sm",
  "data-[hovered]:border-gray-500",
  "data-[focus-within]:border-gray-500",
  "data-[focus-within]:ring-1",
  "data-[focus-within]:ring-gray-500",
  "data-[disabled]:bg-black-50",
  "data-[disabled]:border-black-100",
  "data-[disabled]:text-black",
);

// Unstyled DateInput for range picker so the wrapper provides the field chrome
const rangeDateInputClasses = cn(
  "appearance-none outline-none",
  "bg-transparent",
  "p-0",
  "m-0",
  "border-none",
  "min-w-0",
);

const quickRangeButtonClasses = cn(
  "w-full text-left",
  "px-3 py-2",
  "rounded",
  "hover:bg-gray-50",
  "outline-none",
  "focus:ring-2",
  "focus:ring-gray-500",
);

// Each "part" of the date in the input field
const dateSegmentClasses = cn("outline-none", "focus:bg-gray-200", "rounded");

// The calendar icon button
const calendarButtonClasses = cn(
  "absolute",
  "right-3",
  "top-1/2",
  "-translate-y-1/2",
  "outline-none",
  "data-[disabled]:cursor-not-allowed",
  "hover:border-gray-500",
);

// The buttons for switching months
const headerButtonClasses = cn(
  "w-8",
  "h-8",
  "flex",
  "items-center",
  "justify-center",
  "rounded",
  "hover:bg-gray-50",
  "outline-none",
  "focus:ring-2",
  "focus:ring-gray-500",
  "data-[disabled]:opacity-50",
  "data-[disabled]:bg-white",
  "data-[disabled]:cursor-not-allowed",
);

// The cell displaying each day of the month (single date calendar)
const calendarCellSingleClasses = cn(
  "w-8",
  "leading-8",
  "text-sm",
  "text-center",
  "rounded-md",
  "m-2",
  "hover:bg-gray-50",
  "data-[selected]:font-semibold",
  "data-[selected]:bg-off-white",
  "data-[today]:bg-gray-200",
  "data-[outside-month]:hidden",
  "data-[disabled]:opacity-50",
  "data-[disabled]:bg-white",
  "data-[disabled]:cursor-not-allowed",
);

// The cell styling for range selection calendar (contiguous block)
const calendarCellRangeClasses = cn(
  "w-11",
  "h-11",
  "flex",
  "items-center",
  "justify-center",
  "text-sm",
  "text-center",
  "rounded-none",
  "m-0",
  "font-normal",
  "hover:bg-gray-50",
  // In-range (middle) background for contiguous block
  "data-[selected]:bg-gray-100",
  // Start/end styling (primary background, white text, rounded ends)
  "data-[selection-start]:bg-[#171717]",
  "data-[selection-end]:bg-[#171717]",
  "data-[selection-start]:text-white",
  "data-[selection-end]:text-white",
  "data-[selection-start]:rounded-l-md",
  "data-[selection-end]:rounded-r-md",
  "data-[outside-month]:hidden",
  "data-[disabled]:opacity-50",
  "data-[disabled]:bg-white",
  "data-[disabled]:cursor-not-allowed",
);
/**
 * Input field for selecting dates with a calendar picker.
 *
 * Built with React Aria Components DatePicker. Uses keyboard navigation
 * (arrow keys, tab) to edit date segments (month/day/year) individually.
 * Click the calendar icon to open a visual date picker.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/DatePicker.html#anatomy}
 */
export function InputDate<T extends DateValue>(props: InputDateProps<T>) {
  // DatePicker itself
  const datePickerClasses = cn(...baseDatePickerClasses, props.className);

  return (
    <DatePicker
      {...props}
      className={datePickerClasses}
      aria-label={props["aria-label"] ? props["aria-label"] : "Date"}
    >
      <Group className="flex w-full relative">
        <DateInput className={dateInputClasses}>
          {(segment) => (
            <DateSegment segment={segment} className={dateSegmentClasses} />
          )}
        </DateInput>
        <Button className={calendarButtonClasses}>
          <IconCalendar variant="sm" />
        </Button>
      </Group>
      <Popover className="bg-white border border-black-100 rounded-lg shadow">
        <Dialog className="p-4">
          <Calendar>
            <header className="flex items-center justify-between mb-4">
              <Button slot="previous" className={headerButtonClasses}>
                <IconChevronLeft variant="sm" />
              </Button>
              <Heading className="text-sm font-semibold text-gray-900" />
              <Button slot="next" className={headerButtonClasses}>
                <IconChevronRight variant="sm" />
              </Button>
            </header>
            <CalendarGrid className="border-spacing-1">
              <CalendarGridHeader>
                {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    date={date}
                    className={calendarCellSingleClasses}
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  );
}

interface InputDateRangeProps<T extends DateValue>
  extends DateRangePickerProps<T> {
  disableFuture?: boolean;
  // Use UTC for date math (today/max selectable/max range), instead of local time zone.
  // Helpful when the backend interprets date-only filters as UTC days.
  useUTC?: boolean;
  // Maximum allowed range length in days (inclusive). If exceeded, a warning is shown.
  maxRangeDays?: number;
  // Slightly reduce field padding (for compact UIs like Activity Beta filters)
  dense?: boolean;
}
export function InputDateRange<T extends DateValue>(
  props: InputDateRangeProps<T>,
) {
  const tz = props.useUTC ? "UTC" : getLocalTimeZone();

  // DatePicker itself
  const datePickerClasses = cn(
    ...baseDatePickerClasses,
    "block w-full max-w-none",
    props.className,
  );

  const maxSelectable = props.disableFuture ? today(tz) : undefined;
  const minSelectable =
    props.disableFuture && props.maxRangeDays && props.maxRangeDays > 0
      ? (maxSelectable as CalendarDate).subtract({
          days: Math.max(0, props.maxRangeDays - 1),
        })
      : undefined;
  const todayDate = today(tz);
  const span = Math.max(1, Math.min(7, props.maxRangeDays ?? 7));
  const defaultLast7Days = {
    start: todayDate.subtract({ days: span - 1 }),
    end: todayDate,
  };

  // Controlled/uncontrolled bridge so quick buttons can set value
  const isControlled = Object.prototype.hasOwnProperty.call(props, "value");
  const [internalValue, setInternalValue] = useState<any>(() => {
    return props.defaultValue ?? defaultLast7Days;
  });
  const currentValue = (
    isControlled ? (props as any).value : internalValue
  ) as any;

  // Show "Last X days" summary when end equals today
  const showLastDaysSummary =
    !!currentValue?.start &&
    !!currentValue?.end &&
    currentValue.end.compare(todayDate) === 0;
  const lastDaysText = showLastDaysSummary
    ? (() => {
        const startMs = currentValue.start.toDate("UTC").getTime();
        const endMs = currentValue.end.toDate("UTC").getTime();
        const days = Math.floor(Math.abs(endMs - startMs) / 86_400_000) + 1;
        return `Last ${days} days`;
      })()
    : "";

  const handleChange = (next: any) => {
    let proposed = next;
    // Enforce max range by capping end/start as needed
    if (props.maxRangeDays && next?.start && next?.end) {
      const startFirst = next.start.compare(next.end) <= 0;
      const secondsBetweenStartAndEnd = Math.abs(
        next.end.toDate("UTC").getTime() - next.start.toDate("UTC").getTime(),
      );
      const days = Math.floor(secondsBetweenStartAndEnd / 86_400_000) + 1;
      if (days > props.maxRangeDays) {
        const delta = props.maxRangeDays - 1;
        proposed = startFirst
          ? { start: next.start, end: next.start.add({ days: delta }) }
          : { start: next.end.subtract({ days: delta }), end: next.end };
      }
    }
    props.onChange?.(proposed);
    if (!isControlled) {
      setInternalValue(proposed);
    }
  };

  // Disable dates based on future + maxRangeDays relative to current anchor/start
  const isUnavailable = useMemo(() => {
    return (date: any) => {
      // Future constraint
      if (
        props.disableFuture &&
        maxSelectable &&
        date.compare(maxSelectable) > 0
      ) {
        return true;
      }
      // Past constraint when limiting maximum range relative to today
      if (minSelectable && date.compare(minSelectable) < 0) {
        return true;
      }
      // Max range constraint: if a start is selected and no end yet, cap selectable end dates
      if (props.maxRangeDays && currentValue?.start && !currentValue?.end) {
        const minEnd = currentValue.start; // end cannot be before start
        const maxEnd = currentValue.start.add({ days: props.maxRangeDays - 1 });
        if (date.compare(minEnd) < 0) return true;
        if (date.compare(maxEnd) > 0) return true;
      }
      return false;
    };
  }, [
    props.disableFuture,
    maxSelectable,
    minSelectable,
    props.maxRangeDays,
    currentValue,
  ]);

  return (
    <DateRangePicker
      {...props}
      value={currentValue}
      onChange={handleChange}
      maxValue={maxSelectable as any}
      minValue={minSelectable as any}
      isDateUnavailable={isUnavailable as any}
      className={datePickerClasses}
      aria-label="Date range"
    >
      <Group
        className={cn(
          "flex w-full relative items-center gap-2",
          rangeGroupClasses,
          props.dense ? "py-2 pl-2" : null,
        )}
      >
        {showLastDaysSummary ? (
          <Button
            className={cn(
              rangeDateInputClasses,
              "flex-1 flex items-center text-sm pl-2 pr-2 truncate cursor-pointer",
            )}
          >
            {lastDaysText}
          </Button>
        ) : (
          <>
            <DateInput
              slot="start"
              className={cn(rangeDateInputClasses, "pr-2 text-sm")}
            >
              {(segment) => (
                <DateSegment segment={segment} className={dateSegmentClasses} />
              )}
            </DateInput>
            <span aria-hidden="true" className="text-gray-700">
              –
            </span>
            <DateInput
              slot="end"
              className={cn(rangeDateInputClasses, "flex-1 pl-2 text-sm")}
            >
              {(segment) => (
                <DateSegment segment={segment} className={dateSegmentClasses} />
              )}
            </DateInput>
          </>
        )}
        <Button className={calendarButtonClasses}>
          <IconCalendar variant="sm" />
        </Button>
      </Group>
      <Popover className="bg-white border border-black-100 rounded-lg shadow">
        <Dialog className="p-4">
          {props.maxRangeDays ? (
            <div className="mb-2 text-xs text-gray-600">
              Maximum range: {props.maxRangeDays} days
            </div>
          ) : null}
          <QuickRangesWithCalendar
            onSelectRange={(range) => handleChange(range as any)}
            currentValue={currentValue}
            maxValue={maxSelectable}
            minValue={minSelectable}
            isDateUnavailable={isUnavailable}
            maxRangeDays={props.maxRangeDays}
            tz={tz}
          />
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
}

function QuickRangesWithCalendar(props: {
  onSelectRange: (r: any) => void;
  currentValue: any;
  maxValue?: CalendarDate;
  minValue?: CalendarDate;
  isDateUnavailable?: (d: any) => boolean;
  maxRangeDays?: number;
  tz: string;
}) {
  const t = useMemo<CalendarDate>(() => today(props.tz), [props.tz]);
  const lastDayRange = useMemo(() => ({ start: t, end: t }), [t]);
  const last7DaysRange = useMemo(() => {
    const span = Math.max(1, Math.min(7, props.maxRangeDays ?? 7));
    return { start: t.subtract({ days: span - 1 }), end: t };
  }, [t, props.maxRangeDays]);
  const last30DaysRange = useMemo(() => {
    const span = Math.max(1, Math.min(30, props.maxRangeDays ?? 30));
    return { start: t.subtract({ days: span - 1 }), end: t };
  }, [t, props.maxRangeDays]);
  const last90DaysRange = useMemo(() => {
    const span = Math.max(1, Math.min(90, props.maxRangeDays ?? 90));
    return { start: t.subtract({ days: span - 1 }), end: t };
  }, [t, props.maxRangeDays]);
  const canShowLast1 = (props.maxRangeDays ?? Number.POSITIVE_INFINITY) >= 1;
  const canShowLast7 = (props.maxRangeDays ?? Number.POSITIVE_INFINITY) >= 7;
  const canShowLast30 = (props.maxRangeDays ?? Number.POSITIVE_INFINITY) >= 30;
  const canShowLast90 = (props.maxRangeDays ?? Number.POSITIVE_INFINITY) >= 90;

  const isSameCalendarDate = (a?: any, b?: any) => {
    if (!a || !b) return false;
    return a.year === b.year && a.month === b.month && a.day === b.day;
  };
  const matchesRange = (r?: any, t2?: any) => {
    if (!r || !t2) return false;
    return (
      isSameCalendarDate(r.start, t2.start) && isSameCalendarDate(r.end, t2.end)
    );
  };

  let selectedQuick: "last1" | "last7" | "last30" | "last90" | "custom" =
    "custom";
  if (props.currentValue && matchesRange(props.currentValue, lastDayRange)) {
    selectedQuick = "last1";
  } else if (
    props.currentValue &&
    matchesRange(props.currentValue, last7DaysRange)
  ) {
    selectedQuick = "last7";
  } else if (
    props.currentValue &&
    matchesRange(props.currentValue, last30DaysRange)
  ) {
    selectedQuick = "last30";
  } else if (
    props.currentValue &&
    matchesRange(props.currentValue, last90DaysRange)
  ) {
    selectedQuick = "last90";
  }
  let visibleSelectedQuick: "last1" | "last7" | "last30" | "last90" | "custom" =
    "custom";
  if (selectedQuick === "last1" && canShowLast1) {
    visibleSelectedQuick = "last1";
  } else if (selectedQuick === "last7" && canShowLast7) {
    visibleSelectedQuick = "last7";
  } else if (selectedQuick === "last30" && canShowLast30) {
    visibleSelectedQuick = "last30";
  } else if (selectedQuick === "last90" && canShowLast90) {
    visibleSelectedQuick = "last90";
  }

  return (
    <div className="flex gap-4">
      <div className="w-40 pr-4 border-r border-gray-200">
        <div className="flex flex-col gap-2">
          {canShowLast1 ? (
            <Button
              onPress={() => props.onSelectRange(lastDayRange)}
              className={cn(
                quickRangeButtonClasses,
                visibleSelectedQuick === "last1" &&
                  "bg-off-white font-semibold",
              )}
            >
              Last day
            </Button>
          ) : null}
          {canShowLast7 ? (
            <Button
              onPress={() => props.onSelectRange(last7DaysRange)}
              className={cn(
                quickRangeButtonClasses,
                visibleSelectedQuick === "last7" &&
                  "bg-off-white font-semibold",
              )}
            >
              Last 7 days
            </Button>
          ) : null}
          {canShowLast30 ? (
            <Button
              onPress={() => props.onSelectRange(last30DaysRange)}
              className={cn(
                quickRangeButtonClasses,
                visibleSelectedQuick === "last30" &&
                  "bg-off-white font-semibold",
              )}
            >
              Last 30 days
            </Button>
          ) : null}
          {canShowLast90 ? (
            <Button
              onPress={() => props.onSelectRange(last90DaysRange)}
              className={cn(
                quickRangeButtonClasses,
                visibleSelectedQuick === "last90" &&
                  "bg-off-white font-semibold",
              )}
            >
              Last 90 days
            </Button>
          ) : null}
          <Button
            className={cn(
              quickRangeButtonClasses,
              visibleSelectedQuick === "custom" && "bg-off-white font-semibold",
            )}
          >
            Custom
          </Button>
        </div>
      </div>
      <div className="flex-1">
        <RangeCalendar
          maxValue={props.maxValue as any}
          minValue={props.minValue as any}
          isDateUnavailable={props.isDateUnavailable as any}
        >
          <header className="flex items-center justify-between mb-4">
            <Button slot="previous" className={headerButtonClasses}>
              <IconChevronLeft variant="sm" />
            </Button>
            <Heading className="text-sm font-semibold text-gray-900" />
            <Button slot="next" className={headerButtonClasses}>
              <IconChevronRight variant="sm" />
            </Button>
          </header>
          <div className="px-2 py-1">
            <CalendarGrid className="border-spacing-0">
              <CalendarGridHeader>
                {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    date={date}
                    className={calendarCellRangeClasses}
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </div>
        </RangeCalendar>
      </div>
    </div>
  );
}

interface QuickRangeConfig {
  label: string;
  duration: string; // e.g., "15m", "1h", "1d", "7d"
}

interface InputDateTimeRangeProps<T extends DateValue>
  extends DateRangePickerProps<T> {
  disableFuture?: boolean;
  useUTC?: boolean;
  maxRangeDays?: number;
  dense?: boolean;
  hourCycle?: 12 | 24;
  quickRanges?: QuickRangeConfig[];
}

/**
 * Input field for selecting date and time ranges with a calendar picker.
 *
 * Similar to InputDateRange but includes time selection (hour/minute).
 * Built with React Aria Components DateRangePicker with granularity="minute".
 * Provides quick range buttons and calendar selection.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/DateRangePicker.html}
 */
export function InputDateTimeRange<T extends DateValue>(
  props: InputDateTimeRangeProps<T>,
) {
  const tz = props.useUTC ? "UTC" : getLocalTimeZone();

  const datePickerClasses = cn(
    ...baseDatePickerClasses,
    "block w-full max-w-none",
    props.className,
  );

  const maxSelectable = props.disableFuture ? today(tz) : undefined;
  const minSelectable =
    props.disableFuture && props.maxRangeDays && props.maxRangeDays > 0
      ? (maxSelectable as CalendarDate).subtract({
          days: Math.max(0, props.maxRangeDays - 1),
        })
      : undefined;

  const isControlled = Object.prototype.hasOwnProperty.call(props, "value");
  const [internalValue, setInternalValue] = useState<any>(() => {
    return props.defaultValue;
  });
  const currentValue = (
    isControlled ? (props as any).value : internalValue
  ) as any;

  const handleChange = (next: any) => {
    let proposed = next;
    if (props.maxRangeDays && next?.start && next?.end) {
      const startFirst = next.start.compare(next.end) <= 0;
      const secondsBetweenStartAndEnd = Math.abs(
        next.end.toDate("UTC").getTime() - next.start.toDate("UTC").getTime(),
      );
      const days = Math.floor(secondsBetweenStartAndEnd / 86_400_000) + 1;
      if (days > props.maxRangeDays) {
        const delta = props.maxRangeDays - 1;
        proposed = startFirst
          ? { start: next.start, end: next.start.add({ days: delta }) }
          : { start: next.end.subtract({ days: delta }), end: next.end };
      }
    }
    props.onChange?.(proposed);
    if (!isControlled) {
      setInternalValue(proposed);
    }
  };

  const isUnavailable = useMemo(() => {
    return (date: any) => {
      if (
        props.disableFuture &&
        maxSelectable &&
        date.compare(maxSelectable) > 0
      ) {
        return true;
      }
      if (minSelectable && date.compare(minSelectable) < 0) {
        return true;
      }
      if (props.maxRangeDays && currentValue?.start && !currentValue?.end) {
        const minEnd = currentValue.start;
        const maxEnd = currentValue.start.add({ days: props.maxRangeDays - 1 });
        if (date.compare(minEnd) < 0) return true;
        if (date.compare(maxEnd) > 0) return true;
      }
      return false;
    };
  }, [
    props.disableFuture,
    maxSelectable,
    minSelectable,
    props.maxRangeDays,
    currentValue,
  ]);

  return (
    <DateRangePicker
      {...props}
      value={currentValue}
      onChange={handleChange}
      maxValue={maxSelectable as any}
      minValue={minSelectable as any}
      isDateUnavailable={isUnavailable as any}
      granularity="minute"
      hourCycle={props.hourCycle}
      className={datePickerClasses}
      aria-label="Date and time range"
    >
      <Group
        className={cn(
          "flex w-full relative items-center gap-2",
          rangeGroupClasses,
          props.dense ? "py-2 pl-2" : null,
        )}
      >
        <DateInput
          slot="start"
          className={cn(rangeDateInputClasses, "pr-2 text-sm")}
        >
          {(segment) => (
            <DateSegment segment={segment} className={dateSegmentClasses} />
          )}
        </DateInput>
        <span aria-hidden="true" className="text-gray-700">
          –
        </span>
        <DateInput
          slot="end"
          className={cn(rangeDateInputClasses, "flex-1 pl-2 text-sm")}
        >
          {(segment) => (
            <DateSegment segment={segment} className={dateSegmentClasses} />
          )}
        </DateInput>
        <Button className={calendarButtonClasses}>
          <IconCalendar variant="sm" />
        </Button>
      </Group>
      <Popover className="bg-white border border-black-100 rounded-lg shadow">
        <Dialog className="p-4">
          <QuickDateTimeRangesWithCalendar
            onSelectRange={(range) => handleChange(range as any)}
            currentValue={currentValue}
            maxValue={maxSelectable}
            minValue={minSelectable}
            isDateUnavailable={isUnavailable}
            maxRangeDays={props.maxRangeDays}
            quickRanges={props.quickRanges}
            tz={tz}
          />
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
}

// Parse duration string (e.g., "15m", "1h", "1d") to milliseconds
const parseDurationToMs = (duration: string): number => {
  const match = duration.match(/^(\d+)([mhd])$/);
  if (!match) return 0;

  const value = Number.parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case "m":
      return value * 60 * 1000; // minutes
    case "h":
      return value * 60 * 60 * 1000; // hours
    case "d":
      return value * 24 * 60 * 60 * 1000; // days
    default:
      return 0;
  }
};

function QuickDateTimeRangesWithCalendar(props: {
  onSelectRange: (r: any) => void;
  currentValue: { start: ZonedDateTime; end: ZonedDateTime };
  maxValue?: CalendarDate;
  minValue?: CalendarDate;
  isDateUnavailable?: (d: any) => boolean;
  maxRangeDays?: number;
  tz: string;
  quickRanges?: QuickRangeConfig[];
}) {
  const currentTime = useMemo(() => {
    return now(props.tz);
  }, [props.tz]);

  // Local state for time adjustments
  const [localValue, setLocalValue] = useState(props.currentValue);

  // Update local state when props change
  useMemo(() => {
    setLocalValue(props.currentValue);
  }, [props.currentValue]);

  // Create date-time range from duration string
  const createRangeFromDuration = (duration: string) => {
    const ms = parseDurationToMs(duration);
    const endDate = currentTime;
    const startDate = currentTime.subtract({ milliseconds: ms });
    return { start: startDate, end: endDate };
  };

  // Generate ranges from custom config or use defaults
  const ranges = useMemo(() => {
    if (props.quickRanges && props.quickRanges.length > 0) {
      return props.quickRanges.map((config) => ({
        label: config.label,
        value: createRangeFromDuration(config.duration),
      }));
    }

    // Default ranges
    const defaults: QuickRangeConfig[] = [
      { label: "Last day", duration: "1d" },
      { label: "Last 7 days", duration: "7d" },
      { label: "Last 30 days", duration: "30d" },
      { label: "Last 90 days", duration: "90d" },
    ];

    // Filter by maxRangeDays
    return defaults
      .filter((config) => {
        const days = parseDurationToMs(config.duration) / (24 * 60 * 60 * 1000);
        return days <= (props.maxRangeDays ?? Number.POSITIVE_INFINITY);
      })
      .map((config) => ({
        label: config.label,
        value: createRangeFromDuration(config.duration),
      }));
  }, [props.quickRanges, props.maxRangeDays, currentTime]);

  const isSameDateTime = (a?: any, b?: any) => {
    if (!a || !b) return false;
    return (
      a.year === b.year &&
      a.month === b.month &&
      a.day === b.day &&
      a.hour === b.hour &&
      a.minute === b.minute
    );
  };
  const matchesRange = (r?: any, t2?: any) => {
    if (!r || !t2) return false;
    return isSameDateTime(r.start, t2.start) && isSameDateTime(r.end, t2.end);
  };

  // Find which quick range matches current value (if any)
  const selectedRangeIndex = ranges.findIndex((range) =>
    matchesRange(props.currentValue, range.value),
  );

  return (
    <div className="flex gap-4">
      <div className="w-40 pr-4 border-r border-gray-200">
        <div className="flex flex-col gap-2">
          {ranges.map((range, index) => (
            <Button
              key={range.label}
              onPress={() => props.onSelectRange(range.value)}
              className={cn(
                quickRangeButtonClasses,
                selectedRangeIndex === index && "bg-off-white font-semibold",
              )}
            >
              {range.label}
            </Button>
          ))}
          <Button
            className={cn(
              quickRangeButtonClasses,
              selectedRangeIndex === -1 && "bg-off-white font-semibold",
            )}
          >
            Custom
          </Button>
        </div>
      </div>
      <div className="flex-1">
        <RangeCalendar
          value={localValue}
          onChange={(range) => {
            setLocalValue(range);
            props.onSelectRange(range);
          }}
          maxValue={props.maxValue as any}
          minValue={props.minValue as any}
          isDateUnavailable={props.isDateUnavailable as any}
        >
          <header className="flex items-center justify-between mb-4">
            <Button slot="previous" className={headerButtonClasses}>
              <IconChevronLeft variant="sm" />
            </Button>
            <Heading className="text-sm font-semibold text-gray-900" />
            <Button slot="next" className={headerButtonClasses}>
              <IconChevronRight variant="sm" />
            </Button>
          </header>
          <div className="px-2 py-1">
            <CalendarGrid className="border-spacing-0">
              <CalendarGridHeader>
                {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    date={date}
                    className={calendarCellRangeClasses}
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </div>
        </RangeCalendar>
        {localValue?.start && localValue?.end ? (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="text-xs text-gray-600 mb-1">
                  Start Time on {localValue.start.toDate().toLocaleDateString()}
                </div>
                <TimeField
                  value={localValue.start}
                  hourCycle={24}
                  onChange={(newStart) => {
                    if (newStart) {
                      const updated = { ...localValue, start: newStart };
                      setLocalValue(updated);
                      props.onSelectRange(updated);
                    }
                  }}
                  granularity="minute"
                  aria-label="Start time"
                >
                  {(fieldProps) => (
                    <DateInput
                      className={cn(dateInputClasses, "text-sm w-full min-w-0")}
                    >
                      {(segment) => (
                        <DateSegment
                          segment={segment}
                          className={dateSegmentClasses}
                        />
                      )}
                    </DateInput>
                  )}
                </TimeField>
              </div>
              <span className="text-gray-700 mt-5">–</span>
              <div className="flex-1">
                <div className="text-xs text-gray-600 mb-1">
                  End Time on {localValue.end.toDate().toLocaleDateString()}
                </div>
                <TimeField
                  value={localValue.end}
                  hourCycle={24}
                  onChange={(newEnd) => {
                    if (newEnd) {
                      const updated = { ...localValue, end: newEnd };
                      setLocalValue(updated);
                      props.onSelectRange(updated);
                    }
                  }}
                  granularity="minute"
                  aria-label="End time"
                >
                  {(fieldProps) => (
                    <DateInput
                      className={cn(dateInputClasses, "text-sm w-full min-w-0")}
                    >
                      {(segment) => (
                        <DateSegment
                          segment={segment}
                          className={dateSegmentClasses}
                        />
                      )}
                    </DateInput>
                  )}
                </TimeField>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
